import { useState, useMemo, useEffect } from "react";
import { PageErrorState } from '@/components/shared/PageErrorState';
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { CommentTimeline } from "@/features/comments/components/CommentTimeline";
import { LinkedProductDevCard } from "@/features/product-dev/components/LinkedProductDevCard";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  DollarSign,
  Download,
  File,
  Calculator,
  LayoutGrid,
  List,
  Lock,
  Mail,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Undo2,
  Plus,
  RefreshCw,
  Truck,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import RoleGuard from "@/components/auth/RoleGuard";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { useOrdersWorkflow } from "@/features/purchase-orders/hooks/useOrdersWorkflow";
import { useOrderQuery } from "@/features/purchase-orders/hooks/useOrderQuery";
import { readFileAsBase64 } from "@/lib/read-file-base64";
import { useOrderDateHistory } from "@/features/purchase-orders/hooks/useOrderDates";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { reportError } from "@/lib/logging";
import { toast } from "@/hooks/use-toast";
import type { PurchaseOrder, PurchaseOrderItem } from "@/features/purchase-orders";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { PageLoader } from "@/components/loading/PageLoader";
import { trpc } from "@/lib/trpc";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useSuppliers } from "@/features/suppliers/hooks/useSuppliers";
import { parseEmails } from "@/features/suppliers/types";
import { cn } from "@/lib/utils";
import { sizeRank } from "@/lib/variant-sort";
import { Checkbox } from "@/components/ui/checkbox";
import ReceivingCard from "@/features/purchase-orders/components/ReceivingCard";
import { ShipTransferDialog } from "@/features/purchase-orders/components/ShipTransferDialog";
import { useCheckOrders } from "@/features/purchase-orders/hooks/useCheckOrders";
import {
  unconfirmedShortfallLines,
  unreceivedShippedLines,
} from "@/features/purchase-orders/utils/receiving-baseline";
import { inTransitUnits, isBuyListPoKind } from "@threadheads/shared";
import { AdditionalCostsSection } from "@/features/purchase-orders/components/AdditionalCostsSection";
import { LandedCostCell } from "@/features/purchase-orders/components/LandedCostCell";
import { PriceOverrideCell } from "@/features/purchase-orders/components/PriceOverrideCell";
import { AdjustUnitCostDialog } from "@/features/purchase-orders/components/AdjustUnitCostDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Money } from "@/features/purchase-orders/components/Money";
import { PoActionBar } from "@/features/purchase-orders/components/PoActionBar";
import { PoDatesSection } from "@/features/purchase-orders/components/PoDatesSection";
import { formatDateTime, useDateFnsLocale } from "@/lib/format-dates";
import { useCurrencyToggleHotkey } from "@/features/purchase-orders/hooks/useCurrencyToggleHotkey";
import { useCurrencyDisplayStore } from "@/features/purchase-orders/store/currency-display-store";
import { useRole } from "@/hooks/use-role";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { safePurchaseOrderListReturnTo } from "@/features/purchase-orders/utils/list-state";
import { BackToTopPill } from "@/components/BackToTopPill";
import { InvoicesCard } from "@/features/po-invoices/components/InvoicesCard";
import { JustInTimeOrderView } from "@/features/purchase-orders/components/JustInTimeOrderView";
import { OrderAndWaitOrderView } from "@/features/purchase-orders/components/OrderAndWaitOrderView";

const ORDER_ID_PLACEHOLDER = "00000000-0000-0000-0000-000000000000";

const colorHexMap: Record<string, string> = {
  'black': '#000000', 'natural': '#EBE9DF', 'powder blue': '#0EA5E9',
  'charcoal': '#36454F', 'lilac': '#9370DB', 'pale blue': '#B0E0E6',
  'black ringer': '#000000', 'red ringer': '#FF0000', 'white': '#FFFFFF',
  'red': '#FF0000', 'blue': '#0000FF', 'green': '#008000', 'yellow': '#FFFF00',
  'purple': '#800080', 'pink': '#FFC0CB', 'orange': '#FFA500', 'brown': '#8B4513',
  'black stone': '#414040', 'grey stone': '#535252', 'grey': '#808080',
  'gray': '#808080', 'navy': '#000080', 'beige': '#F5F5DC', 'cream': '#FFFDD0',
  'maroon': '#800000', 'teal': '#008080', 'olive': '#808000', 'silver': '#C0C0C0',
  'gold': '#FFD700',
};

const renderColorCircle = (color: string) => {
  const isRinger = color.toLowerCase().includes('ringer');
  if (isRinger) {
    const ringerColor = color.toLowerCase().includes('black') ? '#000000' : '#FF0000';
    return (
      <div
        className="w-4 h-4 rounded-full bg-white"
        style={{ border: `2px solid ${ringerColor}`, boxSizing: 'border-box' }}
      />
    );
  }
  return (
    <div
      className="w-4 h-4 rounded-full border border-border"
      style={{ backgroundColor: colorHexMap[color.toLowerCase()] || '#CCCCCC' }}
    />
  );
};
const formatStatusLabel = (status: PurchaseOrder["status"]) =>
  status === "partially_received"
    ? "PARTIAL"
    : status === "in_transit"
      ? "IN TRANSIT"
      : status.replace("_", " ").toUpperCase();

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Whether this detail was opened via the canonical transfer URL. Transfers
  // opened at /orders/:id redirect to /transfers/:id (see convergence guard
  // below) so back + list navigations resolve to the right surface.
  const routeLocation = useLocation();
  const isTransferRoute = routeLocation.pathname.startsWith("/transfers/");
  const ordersReturnTo = safePurchaseOrderListReturnTo(
    (routeLocation.state as { ordersReturnTo?: unknown } | null)?.ordersReturnTo,
  );
  const listReturnTo = isTransferRoute ? "/transfers" : ordersReturnTo;
  const utils = trpc.useUtils();
  const { organization } = useOrganization();
  const {
    handleEditDraft,
    handleStatusUpdate,
    handleUndoStatus,
    handleMarkOrderPaid,
    handleCompletePurchaseOrder,
    handleDeleteOrder,
    handleSubmitDraft,
    downloadFinanceCSV,
  } = useOrdersWorkflow();

  // BLU-576: single-PO fetch, org-scoped and location-independent — unlike
  // useOrdersWorkflow's `orders` (a location-scoped, default-limited list),
  // this always resolves the PO regardless of the selected location.
  // The dates panel's ETA history is started by OrderDetailRoute, alongside
  // getOrder; the panel reads that cache entry (PR #1989).
  const { order, loading: orderLoading, error: orderError } = useOrderQuery(id);

  // BLU-373 PR2b: Money component owns currency rendering now; no inline formatter needed.
  const { canManagePurchaseOrders, isAdmin: isAdminFn } = useRole();
  const isAdmin = isAdminFn();
  const dateLocale = useDateFnsLocale();

  // BLU-373 PR2b: currency display state. Reset on every PO navigation so a
  // toggle on PO-A doesn't leak into PO-B (the Zustand store is module-global).
  const orgCurrency = organization?.default_currency ?? "AUD";
  const resetCurrencyDisplay = useCurrencyDisplayStore((s) => s.reset);
  useEffect(() => {
    resetCurrencyDisplay();
  }, [id, resetCurrencyDisplay]);

  // C key toggles currency display on this page. Disabled (no listener)
  // when the PO currency matches the org currency — same-currency POs have
  // no toggle pill, so the hotkey would silently mutate global state with
  // no visible effect (and leak into the next PO via the store).
  // Note: order may not be loaded yet on first render — gate on its presence.
  const [isUpdating, setIsUpdating] = useState(false);
  const [markPaidConfirmOpen, setMarkPaidConfirmOpen] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  // BLU-373 PR3: Tier 1 unit-cost correction
  const [adjustCostItem, setAdjustCostItem] = useState<PurchaseOrderItem | null>(null);
  // BLU-373 PR3 — UI consolidation: parent owns which line item is currently in
  // override-edit mode so we can trigger it from the unified dropdown menu.
  const [overrideEditItemId, setOverrideEditItemId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [shipmentPickerOpen, setShipmentPickerOpen] = useState(false);
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false);
  const [newShipmentForwarderId, setNewShipmentForwarderId] = useState<string>("");
  const [newShipmentTrackingLink, setNewShipmentTrackingLink] = useState("");
  const [newShipmentFreightCost, setNewShipmentFreightCost] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [lineItemsOpen, setLineItemsOpen] = useState(true);
  const [uploadingFileType, setUploadingFileType] = useState<"packing_list" | "invoice" | "other" | null>(null);

  const isTransfer = order?.order_type === 'transfer';

  // BLU-373 PR2b: hotkey only when the toggle pill would be visible
  useCurrencyToggleHotkey(Boolean(order?.currency) && order?.currency !== orgCurrency);

  // `allLocations`, not `locations`: both of these RESOLVE A NAME on a stored
  // order. A transfer whose source or destination has since had its fulfilment
  // delegated still happened at that place, and the filtered list would leave
  // the header reading "Unknown -> Unknown" (BLU-879 review).
  const { allLocations } = useLocations();
  const sourceLocationName = useMemo(() => {
    if (!order?.source_location_id) return null;
    return allLocations.find((l) => l.id === order.source_location_id)?.name ?? null;
  }, [order?.source_location_id, allLocations]);
  const destLocationName = useMemo(() => {
    if (!order?.location_id) return null;
    return allLocations.find((l) => l.id === order.location_id)?.name ?? null;
  }, [order?.location_id, allLocations]);

  const orderId = order?.id ?? id ?? ORDER_ID_PLACEHOLDER;
  const orderLocationId = order?.location_id ?? null;

  // Supplier lookup — prefer FK, fall back to name match
  const { suppliers } = useSuppliers();
  const supplierId = useMemo(() => {
    const fkId = (order as { supplier_id?: string | null } | null)?.supplier_id ?? null;
    if (fkId) return fkId;
    if (!order?.supplier_name) return null;
    const match = suppliers.find(
      (s) => s.title.trim().toLowerCase() === order.supplier_name!.trim().toLowerCase()
    );
    return match?.id ?? null;
  }, [order, suppliers]);
  const supplierEmails = useMemo(() => {
    if (!supplierId) return [];
    const supplier = suppliers.find((s) => s.id === supplierId);
    return supplier ? parseEmails(supplier.email) : [];
  }, [suppliers, supplierId]);

  // BLU-456: due-date derivation for the invoice modal (invoice date + terms).
  const paymentTermsDays = useMemo(() => {
    if (!supplierId) return null;
    return suppliers.find((s) => s.id === supplierId)?.payment_terms_days ?? null;
  }, [suppliers, supplierId]);

  // BLU-377 PR2c-followup: push PO identity into the centred TopHeader slot.
  const headerTitle = useMemo(() => {
    if (!order) return null;
    return isTransfer ? `Transfer ${order.po_number}` : order.po_number;
  }, [order, isTransfer]);
  const headerSubtitle = useMemo(() => {
    if (!order) return null;
    if (isTransfer) {
      return `${sourceLocationName ?? "Unknown"} → ${destLocationName ?? "Unknown"}`;
    }
    if (supplierId) {
      return (
        <Link to={`/suppliers/${supplierId}`} className="text-threadheads-purple hover:underline">
          {order.supplier_name || "Unknown supplier"}
        </Link>
      );
    }
    return order.supplier_name || "Unknown supplier";
  }, [order, isTransfer, sourceLocationName, destLocationName, supplierId]);
  usePageTitle(
    headerTitle,
    headerSubtitle,
    isTransferRoute
      ? { label: "Back to Transfers", to: "/transfers" }
      : { label: "Back to Orders", to: ordersReturnTo },
  );

  const internalCcEmail = organization?.settings?.purchaseOrders?.internalCcEmail?.trim() || null;

  // Build list of all available emails when dialog opens
  const handleOpenEmailDialog = () => {
    const allEmails = new Set<string>();
    for (const email of supplierEmails) allEmails.add(email);
    if (internalCcEmail) allEmails.add(internalCcEmail);
    setSelectedEmails(allEmails);
    setEmailDialogOpen(true);
  };

  const purchaseOrderEmailLogQuery = trpc.purchaseOrders.getPurchaseOrderEmailLog.useQuery(
    { orderId },
    { enabled: Boolean(order) }
  );

  const purchaseOrderPdfQuery = trpc.purchaseOrders.getPurchaseOrderPdf.useQuery(
    { orderId },
    { enabled: false }
  );

  const sendPurchaseOrderEmail = trpc.purchaseOrders.sendPurchaseOrderEmail.useMutation({
    onSuccess: async (result) => {
      await utils.purchaseOrders.getPurchaseOrderEmailLog.invalidate({ orderId });
      if (result.success) {
        toast({ title: "Purchase order emailed", description: `Sent to: ${result.sentTo.join(", ")}` });
      } else {
        toast({ title: "Failed to send email", description: result.error, variant: "destructive" });
      }
    },
    onError: (err) => {
      toast({ title: "Failed to send email", description: err.message, variant: "destructive" });
    },
  });

  const purchaseOrderFilesQuery = trpc.purchaseOrders.listPurchaseOrderFiles.useQuery(
    { orderId },
    { enabled: Boolean(order) }
  );

  const uploadPurchaseOrderFile = trpc.purchaseOrders.uploadPurchaseOrderFile.useMutation({
    onSuccess: async (result) => {
      await utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId });
      toast({ title: "File uploaded", description: result.file_name });
      setUploadingFileType(null);
    },
    onError: (err) => {
      setUploadingFileType(null);
      toast({ title: "Failed to upload file", description: err.message, variant: "destructive" });
    },
  });

  const deletePurchaseOrderFile = trpc.purchaseOrders.deletePurchaseOrderFile.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId });
      toast({ title: "File deleted" });
    },
    onError: (err) => {
      toast({ title: "Failed to delete file", description: err.message, variant: "destructive" });
    },
  });

  const getPurchaseOrderFileDownloadUrl =
    trpc.purchaseOrders.getPurchaseOrderFileDownloadUrl.useMutation({
      onError: (err) => {
        toast({ title: "Failed to get download link", description: err.message, variant: "destructive" });
      },
    });

  const forwardersQuery = trpc.shipments.listForwarders.useQuery(
    { includeInactive: false },
    { staleTime: 60_000, enabled: createShipmentOpen }
  );

  const createShipment = trpc.shipments.createShipment.useMutation({
    onSuccess: async (shipment) => {
      await Promise.all([
        utils.shipments.listShipmentsByPurchaseOrder.invalidate({ purchaseOrderId: orderId }),
        utils.shipments.listShipments.invalidate(),
      ]);
      setCreateShipmentOpen(false);
      toast({ title: "Shipment created", description: shipment.shipment_number ?? "Shipment created" });
      navigate(`/shipments/${shipment.id}`);
    },
    onError: (err) => toast({ title: "Failed to create shipment", description: err.message, variant: "destructive" }),
  });

  const linkedShipmentsQuery = trpc.shipments.listShipmentsByPurchaseOrder.useQuery(
    { purchaseOrderId: orderId },
    { enabled: Boolean(order) }
  );

  const shipmentPickerQuery = trpc.shipments.listShipments.useQuery(
    {
      locationId: orderLocationId,
      limit: 200,
      offset: 0,
    },
    {
      enabled: shipmentPickerOpen && Boolean(orderLocationId),
      staleTime: 30_000,
    }
  );

  const linkShipment = trpc.shipments.linkPurchaseOrder.useMutation({
    onSuccess: async () => {
      await utils.shipments.listShipmentsByPurchaseOrder.invalidate({ purchaseOrderId: orderId });
      await utils.shipments.listShipments.invalidate();
      toast({ title: "Shipment linked" });
      setShipmentPickerOpen(false);
    },
    onError: (err) => toast({ title: "Failed to link shipment", description: err.message, variant: "destructive" }),
  });

  const unlinkShipment = trpc.shipments.unlinkPurchaseOrder.useMutation({
    onSuccess: async () => {
      await utils.shipments.listShipmentsByPurchaseOrder.invalidate({ purchaseOrderId: orderId });
      await utils.shipments.listShipments.invalidate();
      toast({ title: "Shipment unlinked" });
    },
    onError: (err) => toast({ title: "Failed to unlink shipment", description: err.message, variant: "destructive" }),
  });

  const updateHidePricesOnPdf = trpc.purchaseOrders.updateHidePricesOnPdf.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.getOrders.invalidate();
      toast({ title: "PDF price setting updated" });
    },
    onError: (err) => {
      toast({ title: "Failed to update PDF setting", description: err.message, variant: "destructive" });
    },
  });

  // BLU-456: Xero bill/deposit pushes + invoice amount moved into InvoicesCard.

  // PR #1989 review: one date shape on the page; the email log uses the same
  // shared formatter as the dates panel instead of an en-US Intl one.
  const formatDate = (dateString: string | undefined) => formatDateTime(dateString, dateLocale);

  const formatBytes = (value?: number | null) => {
    if (!value || value <= 0) return "--";
    const kb = value / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleUploadPoFile = async (
    fileType: "packing_list" | "invoice" | "other",
    file: File
  ) => {
    if (!order) return;

    const MAX_FILE_BYTES = 7 * 1024 * 1024;
    if (file.size > MAX_FILE_BYTES) {
      toast({ title: "File is too large", description: "Max ~7MB", variant: "destructive" });
      return;
    }

    setUploadingFileType(fileType);
    try {
      const base64 = await readFileAsBase64(file);
      uploadPurchaseOrderFile.mutate({
        orderId: order.id,
        fileType,
        fileName: file.name,
        contentType: file.type || null,
        base64,
      });
    } catch (error) {
      setUploadingFileType(null);
      reportError("Error reading PO file for upload", error);
      toast({ title: "Failed to read file", variant: "destructive" });
    }
  };

  const handleDownloadPoFile = async (fileId: string) => {
    try {
      const result = await getPurchaseOrderFileDownloadUrl.mutateAsync({ fileId });
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch {
      // handled by mutation onError
    }
  };

  const handleDeletePoFile = (fileId: string, fileName: string) => {
    if (!window.confirm(`Delete file "${fileName}"?`)) return;
    deletePurchaseOrderFile.mutate({ fileId });
  };

  const handleDownloadPdf = async () => {
    if (!order) return;
    try {
      const result = await purchaseOrderPdfQuery.refetch();
      if (!result.data) return;

      const { filename, base64 } = result.data;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${order.po_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      reportError("Error downloading PO PDF", error);
      toast({ title: "Failed to download PDF", variant: "destructive" });
    }
  };

  const handleSendEmail = async () => {
    if (!order) return;
    const emails = Array.from(selectedEmails);
    if (emails.length === 0) {
      toast({ title: "No recipients selected", description: "Select at least one email address.", variant: "destructive" });
      return;
    }
    sendPurchaseOrderEmail.mutate({
      orderId: order.id,
      toEmails: emails,
    });
    setEmailDialogOpen(false);
  };

  const linkedShipments = useMemo(
    () => linkedShipmentsQuery.data ?? [],
    [linkedShipmentsQuery.data]
  );
  const linkedShipmentIds = useMemo(
    () => new Set<string>(linkedShipments.map((s) => s.id)),
    [linkedShipments]
  );

  const candidateShipments = useMemo(() => {
    const rows = shipmentPickerQuery.data?.items ?? [];
    return rows.filter((shipment) => !linkedShipmentIds.has(shipment.id));
  }, [shipmentPickerQuery.data?.items, linkedShipmentIds]);

  const getShipmentStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "pending":
        return "pending" as const;
      case "in_transit":
        return "processing" as const;
      case "delivered":
        return "success" as const;
      case "cancelled":
        return "destructive" as const;
      default:
        return "secondary" as const;
    }
  };

  // BLU-377 PR2c-followup: getStatusBadge / getStatusSelectClassName moved into PoActionBar.

  const getItemStatusBadge = (
    status: PurchaseOrderItem["processing_status"]
  ) => {
    const variants = {
      pending: { variant: "secondary" as const, text: "Pending" },
      add_stock: { variant: "success" as const, text: "Added to Stock" },
      edited: { variant: "outline" as const, text: "Edited" },
      not_received: { variant: "destructive" as const, text: "Not Received" },
    };
    const config =
      variants[status as keyof typeof variants] ?? variants.pending;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const handleStatusChange = async (newStatus: PurchaseOrder["status"]) => {
    if (!order) return;

    const totalOrdered = (order.items ?? []).reduce((sum, item) => sum + (item.ordered_quantity ?? 0), 0);
    const totalReceived = (order.items ?? []).reduce((sum, item) => sum + (item.received_quantity ?? 0), 0);

    if (newStatus === "partially_received" && !(totalReceived > 0 && totalReceived < totalOrdered)) {
      toast({
        title: "Invalid partial status",
        description:
          "Partially Received is only valid when some, but not all, units are received.",
      });
      return;
    }

    if (newStatus === "received" && !(totalOrdered > 0 && totalReceived >= totalOrdered)) {
      toast({
        title: "Invalid received status",
        description:
          "Received is only valid when all ordered units are received.",
      });
      return;
    }

    setIsUpdating(true);
    try {
      await handleStatusUpdate(order.id, newStatus, orderLocationId);
    } catch (error) {
      reportError("Error updating status", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!order || isDeleting) return;
    setIsDeleting(true);
    try {
      await handleDeleteOrder(order.id, orderLocationId);
      setDeleteConfirmOpen(false);
      navigate(isTransfer ? "/transfers" : ordersReturnTo);
    } catch (error) {
      reportError("Error deleting order", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = async () => {
    if (!order || isUpdating) return;
    setIsUpdating(true);
    try {
      await handleStatusUpdate(order.id, "cancelled", orderLocationId);
      setCancelConfirmOpen(false);
      navigate(isTransfer ? "/transfers" : ordersReturnTo);
    } catch (error) {
      reportError("Error cancelling order", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = () => {
    if (!order) return;
    handleEditDraft(order);
    navigate(`/orders/${order.id}/edit`, { state: { ordersReturnTo } });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuickSubmit = async () => {
    if (!order) return;
    if (!isTransfer && !order.supplier_name?.trim()) return;
    setIsSubmitting(true);
    try {
      await handleSubmitDraft(
        order.id,
        order.supplier_name ?? '',
        (order as { supplier_id?: string | null }).supplier_id ?? null,
        orderLocationId
      );
    } catch (error) {
      reportError("Error submitting draft", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isUndoing, setIsUndoing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [shipConfirmOpen, setShipConfirmOpen] = useState(false);

  const handleUndo = async () => {
    if (!order) return;
    setIsUndoing(true);
    try {
      await handleUndoStatus(order.id, orderLocationId);
      setUndoConfirmOpen(false);
    } catch (error) {
      reportError("Error undoing status", error);
    } finally {
      setIsUndoing(false);
    }
  };

  // BLU-572: short-close a partially received PO. Flips status to received so
  // the leftover (ordered − received) stops counting as incoming. No stock is
  // moved and received quantities are preserved.
  const handleComplete = async () => {
    if (!order || isCompleting) return;
    setIsCompleting(true);
    try {
      await handleCompletePurchaseOrder(order.id, orderLocationId);
      setCompleteConfirmOpen(false);
    } catch (error) {
      reportError("Error completing purchase order", error);
    } finally {
      setIsCompleting(false);
    }
  };

  // Determine the previous status for undo label
  const undoPreviousStatus = useMemo(() => {
    const TRANSFER_CHAIN = ['draft', 'submitted', 'in_transit', 'partially_received', 'received'];
    const PO_CHAIN = ['draft', 'submitted', 'in_production', 'ready', 'in_transit', 'partially_received', 'received'];
    const chain = isTransfer ? TRANSFER_CHAIN : PO_CHAIN;
    const idx = chain.indexOf(order?.status ?? '');
    if (idx <= 0) return null;
    return chain[idx - 1];
  }, [order?.status, isTransfer]);
  const canUndo = undoPreviousStatus !== null && order?.status !== 'cancelled';

  const sortedItems = useMemo(() =>
    [...(order?.items ?? [])].sort((a, b) =>
      (a.sku || "").localeCompare(b.sku || "")
    ),
    [order?.items]
  );

  const { shipTransfer, isShipping } = useCheckOrders();

  // The shortfall rule lives in one helper shared with the receiving card and
  // mirrored by the server, so the complete button cannot disagree with the
  // gate that would reject it.
  const unconfirmedShortfall = useMemo(
    () => (order ? unconfirmedShortfallLines(order, sortedItems) : []),
    [order, sortedItems]
  );

  // The other half of the same gate: units that DID leave the source and have
  // not arrived. Also mirrored from the server, for the same reason.
  const unreceivedShipped = useMemo(
    () => (order ? unreceivedShippedLines(order, sortedItems) : []),
    [order, sortedItems]
  );

  const handleShipTransfer = async (
    shipItems: Array<{ itemId: string; shippedQuantity: number }>
  ) => {
    if (!order) return;
    const result = await shipTransfer(order.id, shipItems);

    // A lost status race means someone already shipped this transfer, so there
    // are no totals of our own to report.
    if (!("shippedTotal" in result)) {
      toast({
        title: "Transfer already shipped",
        description: "Another user shipped this transfer first.",
      });
      return;
    }

    toast({
      title: "Transfer shipped",
      description:
        result.unshippedTotal > 0
          ? `${result.shippedTotal} units are on the way. ${result.unshippedTotal} stay committed at the source until confirmed.`
          : `${result.shippedTotal} units are on the way.`,
    });
  };

  // Matrix view data: group items by category (type) → color (rows) x size (columns).
  //
  // BLU-302: multiple SKUs can collide on the same (type, color, size) triple
  // — e.g. two product styles within the same broad category sharing colour
  // and size. The previous implementation overwrote the cell with each
  // colliding item, silently dropping every SKU except the last one. Now we
  // SUM ordered + received and expose a `skuCount` so the cell can flag
  // when it represents more than one underlying line item.
  const detailMatrixData = useMemo(() => {
    const grouped: Record<string, {
      colors: string[];
      sizes: string[];
      matrix: Record<string, Record<string, {
        ordered: number;
        received: number;
        imageUrl?: string;
        skuCount: number;
      }>>;
    }> = {};

    for (const item of sortedItems) {
      // Grouping key is the raw `item.category` field (e.g. "Tee",
      // "Hoodie") — distinct product styles that share the same category
      // string land in the same matrix. Quantities aggregate; the list
      // view still surfaces individual SKUs for users who need that detail.
      const type = item.category || 'Unknown';
      const color = item.color || 'Unknown';
      const size = item.size || 'Unknown';

      if (!grouped[type]) {
        grouped[type] = { colors: [], sizes: [], matrix: {} };
      }
      const group = grouped[type];
      if (!group.colors.includes(color)) group.colors.push(color);
      if (!group.sizes.includes(size)) group.sizes.push(size);
      if (!group.matrix[color]) group.matrix[color] = {};
      const existing = group.matrix[color][size];
      group.matrix[color][size] = {
        ordered: (existing?.ordered ?? 0) + item.ordered_quantity,
        received: (existing?.received ?? 0) + item.received_quantity,
        imageUrl: existing?.imageUrl ?? item.image_url ?? undefined,
        skuCount: (existing?.skuCount ?? 0) + 1,
      };
    }

    for (const group of Object.values(grouped)) {
      group.colors.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      group.sizes.sort((a, b) => sizeRank(a) - sizeRank(b));
    }

    return grouped;
  }, [sortedItems]);

  // Loading state
  if (orderLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="purchaseOrders" />
        </div>
      </RoleGuard>
    );
  }

  // Error state
  if (orderError) {
    return (
      <div className="p-4">
        <PageErrorState
          title="Error Loading Order"
          description={orderError?.message || "Failed to load order data"}
        />
      </div>
    );
  }

  // Not found state
  if (!order) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              onClick={() => navigate(listReturnTo)}
              className="mb-4 -ml-2 flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {isTransferRoute ? "Back to Transfers" : "Back to Orders"}
            </Button>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-8 text-center">
              <h2 className="text-xl font-semibold text-amber-700 mb-2">
                Order Not Found
              </h2>
              <p className="text-amber-600">
                The purchase order you are looking for does not exist or has been
                removed.
              </p>
            </div>
          </main>
        </div>
      </RoleGuard>
    );
  }


  // Converge transfer URLs on /transfers/:id. A transfer reached via the legacy
  // /orders/:id path (old links, shipment/incoming-stock cross-links) redirects
  // here so its back + list navigations resolve to the transfers surface. PO
  // orders on /orders/:id are unaffected.
  if (isTransfer && !isTransferRoute) {
    return <Navigate to={`/transfers/${order.id}`} replace />;
  }

  const totalOrdered = sortedItems.reduce((sum, item) => sum + item.ordered_quantity, 0);
  const totalReceived = sortedItems.reduce((sum, item) => sum + item.received_quantity, 0);
  const subtotal = sortedItems.reduce(
    (sum, item) => sum + (item.line_cost_total ?? (item.unit_cost ?? 0) * item.ordered_quantity),
    0
  );

  const additionalCosts = (order as { additional_costs?: Array<{ id: string; po_id: string; cost_type: string; label: string | null; amount: number; sort_order: number; created_at: string; updated_at: string }> }).additional_costs ?? [];
  const additionalCostsTotal = additionalCosts.reduce((sum, c) => sum + (c.amount ?? 0), 0);
  const hasAdditionalCosts = additionalCosts.length > 0;

  // BLU-377: paid lock is on payment_status, not fulfillment status
  const isPaid = order.payment_status === "paid";

  // BLU-377: paid POs are frozen (rate locked) — no additional costs / overrides
  const isAdditionalCostsEditable = canManagePurchaseOrders() && order.status !== "cancelled" && !isPaid;
  // BLU-373 PR3 — Override is the PRE-receive path. Once any stock is received
  // it locks; the adjust-cost path takes over (which forces a reason and rebases
  // WAC). The two paths are mutually exclusive by status, never both at once.
  const canOverridePrice =
    canManagePurchaseOrders() &&
    order.status !== "cancelled" &&
    order.status !== "partially_received" &&
    order.status !== "received" &&
    !isPaid;
  // BLU-373 PR3: Tier 1 cost correction is allowed AFTER receive (and even on
  // paid POs — the mutation only edits unit_cost, never exchange_rate).
  // Server enforces Coordinator location access; this is the UI-side gate.
  const canAdjustUnitCost =
    !isTransfer &&
    canManagePurchaseOrders() &&
    (order.status === "partially_received" || order.status === "received");

  const filesByType = {
    packing_list: (purchaseOrderFilesQuery.data ?? []).filter((row) => row.file_type === "packing_list"),
    invoice: (purchaseOrderFilesQuery.data ?? []).filter((row) => row.file_type === "invoice"),
    other: (purchaseOrderFilesQuery.data ?? []).filter((row) => row.file_type === "other"),
  };

  const canEdit = order.status !== "received" && order.status !== "cancelled" && !isPaid;
  const canDelete = order.status === "draft" || order.status === "cancelled";
  const canCancel =
    order.status !== "draft" &&
    order.status !== "received" &&
    order.status !== "cancelled";
  // BLU-572: "Complete PO" is the sole short-close path for a partially
  // received order (Received stays hidden from the status dropdown for
  // partials). Management permission required; independent of payment lock.
  // BLU-535 section D: a transfer can now be completed too. The old exclusion
  // assumed short-closing would strand stock that had already left the source,
  // which was true while a ship moved the whole order. A ship now records what
  // actually went, and anything left behind is either still committed at the
  // source or explicitly confirmed as never shipped, so nothing is stranded.
  // The gate is that every shortfall has been confirmed; the server enforces
  // the same rule, and this keeps the button from firing and bouncing.
  // Completable only for a transfer that shipped per SKU: an unstamped one has
  // no honest arithmetic behind it and the server still refuses it outright.
  // Then both ends have to be settled, the units that never left the source and
  // the units that left and have not arrived.
  const canComplete =
    canManagePurchaseOrders() &&
    (isTransfer
      ? Boolean(order.shipped_quantities_recorded_at) &&
        (order.status === "in_transit" || order.status === "partially_received") &&
        unconfirmedShortfall.length === 0 &&
        unreceivedShipped.length === 0
      : order.status === "partially_received");
  // When that gate is what is holding a transfer back, the button stays on
  // screen disabled and says so. Hiding it left an operator with a transfer
  // that would not close and nothing anywhere explaining why. Null means there
  // is no outstanding-units story to tell, so the button behaves exactly as it
  // did before: shown when it can fire, absent otherwise.
  //
  // Same arithmetic and the same wording family as the server's two refusals in
  // purchase-orders.service.ts, so what the tooltip says and what a direct API
  // call would be told cannot disagree.
  const completeBlockedReason = ((): string | null => {
    if (!isTransfer || !canManagePurchaseOrders()) return null;
    if (!order.shipped_quantities_recorded_at) return null;
    if (order.status !== "in_transit" && order.status !== "partially_received") return null;

    const parts: string[] = [];

    const unconfirmedUnits = unconfirmedShortfall.reduce(
      (sum, item) => sum + Math.max(item.ordered_quantity - (item.shipped_quantity ?? 0), 0),
      0
    );
    if (unconfirmedUnits > 0) {
      parts.push(
        `${unconfirmedUnits.toLocaleString()} ordered unit${unconfirmedUnits === 1 ? "" : "s"} still need${unconfirmedUnits === 1 ? "s" : ""} confirming as not shipped.`
      );
    }

    const unitsInTransit = unreceivedShipped.reduce((sum, item) => sum + inTransitUnits(item), 0);
    if (unitsInTransit > 0) {
      parts.push(
        `${unitsInTransit.toLocaleString()} shipped unit${unitsInTransit === 1 ? "" : "s"} ${unitsInTransit === 1 ? "has" : "have"} not been received yet.`
      );
    }

    if (parts.length === 0) return null;
    if (parts.length === 2) {
      return `${parts.join(" ")} Handle both on the receiving card below, then complete the transfer.`;
    }
    return unitsInTransit > 0
      ? `${parts[0]} Receive them, or mark them returned or scrapped on the receiving card below, then complete the transfer.`
      : `${parts[0]} Handle that on the receiving card below, then complete the transfer.`;
  })();
  const canSubmitDraft = order.status === "draft" && (isTransfer || !!order.supplier_name?.trim());
  const canSend = !isTransfer && order.status !== "draft" && order.status !== "cancelled" && !isPaid;
  // BLU-377: status select is independent of payment now — paid POs can still
  // have their fulfillment status undone (the trigger keeps the rate locked).
  // Only block on draft/cancelled (and in_transit for transfers).
  const showStatusSelect =
    order.status !== "draft" &&
    order.status !== "cancelled" &&
    (!isTransfer || order.status !== "in_transit");
  const validStatuses: PurchaseOrder["status"][] = (() => {
    const allStatuses = isTransfer
      ? (["submitted", "in_transit", "partially_received", "received", "cancelled"] as PurchaseOrder["status"][])
      : (["submitted", "in_production", "ready", "partially_received", "received", "cancelled"] as PurchaseOrder["status"][]);
    const filtered = allStatuses.filter((status) => {
      // For transfers: hide in_transit from dropdown (use Ship Transfer button instead)
      if (isTransfer && status === "in_transit") return false;
      if (status === "partially_received") {
        return totalReceived > 0 && totalReceived < totalOrdered;
      }
      if (status === "received") {
        return totalOrdered > 0 && totalReceived >= totalOrdered;
      }
      return true;
    });
    // Always include the current status so the Select can display it
    if (!filtered.includes(order.status)) {
      filtered.unshift(order.status);
    }
    return filtered;
  })();

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          {/* "Back to Orders" lives in the TopHeader (typewriter button). */}

          {/* BLU-377 / PR2c-followup: floating action bar — sticky just below TopHeader.
              Replaces the giant header card. PO name + supplier moved to centred
              TopHeader slot via usePageTitle. BackToTopPill mounted at page bottom. */}
          <PoActionBar
            order={order}
            isTransfer={isTransfer}
            isAdmin={isAdmin}
            isPaid={isPaid}
            canEdit={canEdit}
            canSubmitDraft={canSubmitDraft}
            canUndo={canUndo}
            canSend={canSend}
            canCancel={canCancel}
            canComplete={canComplete}
            completeBlockedReason={completeBlockedReason}
            canDelete={canDelete}
            showStatusSelect={showStatusSelect}
            validStatuses={validStatuses}
            onStatusChange={handleStatusChange}
            isUpdating={isUpdating}
            isUndoing={isUndoing}
            isSubmitting={isSubmitting}
            isMarkingPaid={isMarkingPaid}
            isCompleting={isCompleting}
            isExportingPdf={purchaseOrderPdfQuery.isFetching}
            isSendingEmail={sendPurchaseOrderEmail.isPending}
            onEdit={handleEdit}
            onSubmitDraft={handleQuickSubmit}
            onShipTransfer={() => setShipConfirmOpen(true)}
            onUndo={() => setUndoConfirmOpen(true)}
            onComplete={() => setCompleteConfirmOpen(true)}
            onMarkPaid={() => setMarkPaidConfirmOpen(true)}
            onExportCsv={() => downloadFinanceCSV(order)}
            onDownloadPdf={() => void handleDownloadPdf()}
            onSend={handleOpenEmailDialog}
            onCancel={() => setCancelConfirmOpen(true)}
            onDelete={() => setDeleteConfirmOpen(true)}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px] mt-6">
            <div className="space-y-6 min-w-0">
              <Collapsible open={lineItemsOpen} onOpenChange={setLineItemsOpen}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="flex flex-row items-center justify-between gap-4 cursor-pointer select-none">
                      <CardTitle className="flex items-center gap-2">
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            !lineItemsOpen && "-rotate-90"
                          )}
                        />
                        Line Items
                        <Badge variant="secondary">
                          {sortedItems.length} SKU{sortedItems.length === 1 ? "" : "s"}
                        </Badge>
                      </CardTitle>
                      {sortedItems.length > 0 && (
                        <div className="flex items-center rounded-md border" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 px-2.5 rounded-r-none"
                            onClick={() => setViewMode('list')}
                          >
                            <List className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 px-2.5 rounded-l-none"
                            onClick={() => setViewMode('table')}
                          >
                            <LayoutGrid className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                  {viewMode === 'list' ? (
                    <>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Image</TableHead>
                              <TableHead>SKU</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Ordered</TableHead>
                              <TableHead className="text-right">Received</TableHead>
                              {!isTransfer && <TableHead className="text-right">Unit Cost</TableHead>}
                              {!isTransfer && <TableHead className="text-right">Line Total</TableHead>}
                              {!isTransfer && hasAdditionalCosts && <TableHead className="text-right">Landed Total</TableHead>}
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedItems.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={isTransfer ? 6 : hasAdditionalCosts ? 9 : 8} className="text-center py-8 text-muted-foreground">
                                  No line items
                                </TableCell>
                              </TableRow>
                            ) : (
                              sortedItems.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    <ProductImageWithHover
                                      imageUrl={item.image_url}
                                      productSku={item.sku || ""}
                                      size="medium"
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">{item.sku}</TableCell>
                                  <TableCell>
                                    {item.title ? (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="text-sm font-medium">{item.title}</span>
                                        {[item.category, item.color, item.size].filter(Boolean).length > 0 && (
                                          <span className="text-xs text-muted-foreground">
                                            {[item.category, item.color, item.size].filter(Boolean).join(" · ")}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-sm">
                                        {item.category || "--"} · {item.color || "--"} · {item.size || "--"}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">{item.ordered_quantity}</TableCell>
                                  <TableCell className="text-right">{item.received_quantity}</TableCell>
                                  {!isTransfer && (
                                    <TableCell className="text-right min-w-[130px]">
                                      <div className="flex items-center justify-end gap-1">
                                        <PriceOverrideCell
                                          item={item}
                                          canOverride={canOverridePrice}
                                          currency={order?.currency ?? null}
                                          exchangeRate={order?.exchange_rate ?? null}
                                          orderId={order.id}
                                          externalEdit={
                                            (canOverridePrice || canAdjustUnitCost)
                                              ? {
                                                  isEditing: overrideEditItemId === item.id,
                                                  onEditingChange: (next) =>
                                                    setOverrideEditItemId(next ? item.id : null),
                                                }
                                              : undefined
                                          }
                                        />
                                        {(canOverridePrice || canAdjustUnitCost) && (
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                aria-label={`Cost actions for ${item.sku ?? "this line"}`}
                                              >
                                                <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                              <DropdownMenuItem
                                                disabled={!canOverridePrice}
                                                onClick={() => setOverrideEditItemId(item.id)}
                                              >
                                                <Pencil className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                                                Override price
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                disabled={!canAdjustUnitCost}
                                                onClick={() => setAdjustCostItem(item)}
                                              >
                                                <Calculator className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                                                Adjust cost
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        )}
                                      </div>
                                    </TableCell>
                                  )}
                                  {!isTransfer && (
                                    <TableCell className="text-right font-medium">
                                      <Money
                                        amount={item.line_cost_total}
                                        poCurrency={order.currency}
                                        exchangeRate={order.exchange_rate}
                                      />
                                    </TableCell>
                                  )}
                                  {!isTransfer && hasAdditionalCosts && (
                                    <TableCell className="text-right">
                                      <LandedCostCell
                                        item={item}
                                        additionalCosts={additionalCosts}
                                        subtotal={subtotal}
                                        poCurrency={order.currency}
                                        exchangeRate={order.exchange_rate}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>{getItemStatusBadge(item.processing_status)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      {sortedItems.length > 0 && !isTransfer && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex justify-between text-sm font-medium">
                            <span className="text-foreground">Subtotal</span>
                            <Money
                              amount={subtotal}
                              poCurrency={order.currency}
                              exchangeRate={order.exchange_rate}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      {sortedItems.length === 0 ? (
                        <div className="py-8 text-center text-muted-foreground">
                          No line items
                        </div>
                      ) : (
                        <>
                          {Object.entries(detailMatrixData).map(([type, data]) => (
                            <div key={type} className="rounded-lg border p-4">
                              <h3 className="text-base font-semibold mb-3">{type}</h3>
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                  <thead>
                                    <tr>
                                      <th className="border border-border p-2 bg-muted text-left text-sm font-medium min-w-[160px]">
                                        Color
                                      </th>
                                      {data.sizes.map((size) => (
                                        <th key={size} className="border border-border p-2 bg-muted text-center text-sm font-medium min-w-[80px]">
                                          {size}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {data.colors.map((color) => {
                                      const sampleImageUrl =
                                        Object.values(data.matrix[color] ?? {}).find((c) => c.imageUrl)?.imageUrl;
                                      return (
                                        <tr key={color}>
                                          <td className="border border-border p-2">
                                            <div className="flex items-center gap-2">
                                              <ProductImageWithHover
                                                imageUrl={sampleImageUrl}
                                                productSku={`${type} - ${color}`}
                                                size="medium"
                                              />
                                              {renderColorCircle(color)}
                                              <span className="text-sm capitalize">{color}</span>
                                            </div>
                                          </td>
                                          {data.sizes.map((size) => {
                                            const cell = data.matrix[color]?.[size];
                                            if (!cell) {
                                              return (
                                                <td key={size} className="border border-border p-2 text-center text-muted-foreground">
                                                  -
                                                </td>
                                              );
                                            }
                                            return (
                                              <td key={size} className="border border-border p-2 text-center" title={cell.skuCount > 1 ? `${cell.skuCount} SKUs aggregated` : undefined}>
                                                <div className="text-sm font-medium tabular-nums">{cell.ordered}</div>
                                                {cell.received > 0 && (
                                                  <div className="text-[10px] text-muted-foreground tabular-nums">
                                                    {cell.received} rcvd
                                                  </div>
                                                )}
                                                {cell.skuCount > 1 && (
                                                  // Muted, not brand-purple: per DESIGN.md §1 the single accent
                                                  // is reserved for primary actions / hover affordance / chrome.
                                                  // This is a quiet "more than one SKU rolled up here" signal —
                                                  // detail is in the native `title` tooltip + the list view.
                                                  <div className="text-[10px] text-muted-foreground tabular-nums">
                                                    {cell.skuCount} SKUs
                                                  </div>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                          {!isTransfer && (
                            <div className="pt-4 border-t">
                              <div className="flex justify-between text-sm font-medium">
                                <span className="text-foreground">Subtotal</span>
                                <Money
                                  amount={subtotal}
                                  poCurrency={order.currency}
                                  exchangeRate={order.exchange_rate}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Additional Costs — own section between Line Items and Receiving */}
              {!isTransfer && sortedItems.length > 0 && (isAdditionalCostsEditable || hasAdditionalCosts) && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="h-4 w-4" />
                      Additional Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AdditionalCostsSection
                      costs={additionalCosts}
                      orderId={orderId}
                      currency={order.currency ?? "AUD"}
                      exchangeRate={order.exchange_rate}
                      isEditable={isAdditionalCostsEditable}
                      onMutationSuccess={() => utils.purchaseOrders.getOrders.invalidate()}
                    />
                    {hasAdditionalCosts && (
                      <div className="mt-3 pt-3 border-t-2 flex justify-between text-lg font-bold">
                        <span>PO Total</span>
                        <Money
                          amount={order.total_cost ?? (subtotal + additionalCostsTotal)}
                          poCurrency={order.currency}
                          exchangeRate={order.exchange_rate}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <ReceivingCard
                order={order}
                items={sortedItems}
                sourceLocationName={sourceLocationName}
                canResolveUnreceived={canManagePurchaseOrders()}
              />

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center text-base">
                    <Truck className="h-4 w-4 mr-2" />
                    Shipments
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!orderLocationId}
                      onClick={() => {
                        setNewShipmentForwarderId("");
                        setNewShipmentTrackingLink("");
                        setNewShipmentFreightCost("");
                        setCreateShipmentOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Shipment
                    </Button>
                    <Popover open={shipmentPickerOpen} onOpenChange={setShipmentPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button size="sm" disabled={!orderLocationId}>
                          <Plus className="h-4 w-4 mr-2" />
                          Link Shipment
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[360px] p-0" align="end">
                        <Command>
                          <CommandInput placeholder="Search shipment number..." />
                          <CommandList>
                            <CommandEmpty>
                              {shipmentPickerQuery.isFetching ? "Loading shipments…" : "No shipments found."}
                            </CommandEmpty>
                            <CommandGroup heading="Shipments">
                              {candidateShipments.map((shipment) => (
                                <CommandItem
                                  key={shipment.id}
                                  onSelect={() =>
                                    linkShipment.mutate({
                                      shipmentId: shipment.id,
                                      purchaseOrderId: order.id,
                                    })
                                  }
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">{shipment.shipment_number}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {shipment.status} {shipment.expected_delivery_date ? `• ETA ${shipment.expected_delivery_date}` : ""}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </CardHeader>
                <CardContent>
                  {linkedShipmentsQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading linked shipments…</p>
                  ) : linkedShipments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No shipments linked yet.</p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Shipment</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Forwarder</TableHead>
                            <TableHead>Freight</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {linkedShipments.map((shipment) => (
                            <TableRow key={shipment.id}>
                              <TableCell>
                                <Link
                                  to={`/shipments/${shipment.id}`}
                                  className="font-medium text-primary hover:text-primary/80 hover:underline"
                                >
                                  {shipment.shipment_number}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Badge variant={getShipmentStatusBadgeVariant(String(shipment.status))}>
                                  {String(shipment.status).replace("_", " ").toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell>{shipment.freight_forwarder?.name ?? "--"}</TableCell>
                              <TableCell>
                                <Money
                                  amount={(shipment as { freight_cost?: number | null }).freight_cost ?? null}
                                  poCurrency={order.currency}
                                  exchangeRate={order.exchange_rate}
                                  emptyAsDash
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    unlinkShipment.mutate({
                                      shipmentId: shipment.id,
                                      purchaseOrderId: order.id,
                                    })
                                  }
                                  disabled={unlinkShipment.isPending}
                                >
                                  Unlink
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Comment + activity timeline — sits in the main (left) column
                  directly under Shipments so the shorter column doesn't strand
                  whitespace below the taller billing/summary column. The 1fr
                  column is wide enough that it isn't squashed. Permission-gated
                  server-side via PurchaseOrdersView. */}
              {order?.id && (
                <CommentTimeline entityType="purchase_order" entityId={order.id} />
              )}
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isTransfer ? (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Source location</p>
                        <p className="font-medium">{sourceLocationName || "--"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Destination location</p>
                        <p className="font-medium">{destLocationName || "--"}</p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground">Supplier</p>
                      <p className="font-medium">
                        {supplierId ? (
                          <Link to={`/suppliers/${supplierId}`} className="text-primary hover:underline">
                            {order.supplier_name || "--"}
                          </Link>
                        ) : (
                          order.supplier_name || "--"
                        )}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Order title</p>
                    <p className="font-medium">{order.title || "--"}</p>
                  </div>
                  {/* PR #1989 review: no pencils on a cancelled order; "Supplier confirmed" is the
                      buyer's own column, the partner's acknowledgement is only its display fallback. */}
                  <PoDatesSection
                    orderId={order.id}
                    locationId={order.location_id}
                    isTransfer={isTransfer}
                    canEditDates={canManagePurchaseOrders() && order.status !== "cancelled"}
                    submittedDate={order.submitted_date}
                    supplierConfirmedAt={order.supplier_confirmed_at}
                    supplierAcknowledgedAt={order.supplier_acknowledged_at}
                    etaDate={order.eta_date}
                    shippedDate={order.shipped_date}
                    arrivalEtaDate={order.arrival_eta_date}
                    completedDate={order.completed_date}
                  />
                  {order.notes ? (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* Invoices (PO only) — BLU-456 multi-invoice card. Owns the
                  per-invoice Xero pushes, the AI ingestion modal, and the
                  legacy PO-level invoice flow (file upload, invoice amount,
                  legacy bill/deposit pushes) until po_invoices exist. */}
              {!isTransfer && (
                <InvoicesCard order={order} paymentTermsDays={paymentTermsDays} />
              )}

              {/* Hide Prices on PDF (PO only) */}
              {!isTransfer && (() => {
                const poOverride = (order as { hide_prices_on_pdf?: boolean | null }).hide_prices_on_pdf ?? null;
                const supplierRecord = supplierId ? suppliers.find((s) => s.id === supplierId) : null;
                const supplierDefault = supplierRecord?.hide_prices_on_pdf ?? false;
                const resolved = poOverride ?? supplierDefault;
                const isInheriting = poOverride === null;

                return (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">PDF Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <p className="text-sm font-medium">Hide prices</p>
                          <p className="text-xs text-muted-foreground">
                            {isInheriting
                              ? `Inherited from supplier (${supplierDefault ? "hidden" : "shown"})`
                              : "Overridden on this PO"}
                          </p>
                        </div>
                        <Switch
                          checked={resolved}
                          onCheckedChange={(checked) =>
                            updateHidePricesOnPdf.mutate({ orderId: order.id, hidePricesOnPdf: checked })
                          }
                          disabled={updateHidePricesOnPdf.isPending}
                        />
                      </div>
                      {!isInheriting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() =>
                            updateHidePricesOnPdf.mutate({ orderId: order.id, hidePricesOnPdf: null })
                          }
                          disabled={updateHidePricesOnPdf.isPending}
                        >
                          Reset to supplier default
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

              <Card>
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Qty</span>
                    <span>{totalOrdered.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Received Qty</span>
                    <span>{totalReceived.toLocaleString()}</span>
                  </div>
                  {!isTransfer && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-foreground">{hasAdditionalCosts ? "Items Subtotal" : "Subtotal"}</span>
                        <Money
                          amount={subtotal}
                          poCurrency={order.currency}
                          exchangeRate={order.exchange_rate}
                          showCode
                        />
                      </div>
                      {hasAdditionalCosts && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Additional Costs</span>
                            <Money
                              amount={additionalCostsTotal}
                              poCurrency={order.currency}
                              exchangeRate={order.exchange_rate}
                              showCode
                            />
                          </div>
                          <div className="flex justify-between text-lg font-bold pt-2 border-t">
                            <span>PO Total</span>
                            <Money
                              amount={order.total_cost ?? (subtotal + additionalCostsTotal)}
                              poCurrency={order.currency}
                              exchangeRate={order.exchange_rate}
                              showCode
                            />
                          </div>
                        </>
                      )}
                      {/* BLU-373 PR2b: legacy "\u2248 AUD" caption is now redundant \u2014 the Money cells
                          above flip to AUD via the toggle pill. Keep just the rate hint for the
                          curious, only on non-AUD POs with a meaningful rate. */}
                      {order.currency && order.currency !== orgCurrency && order.exchange_rate && order.exchange_rate !== 1 ? (
                        <p className="text-xs text-muted-foreground text-right tabular-nums">
                          1 {order.currency} = {Number(order.exchange_rate).toFixed(4)} {orgCurrency}
                        </p>
                      ) : null}
                    </>
                  )}
                </CardContent>
              </Card>

              {!isTransfer && <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2" />
                    Files
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {([
                    { type: "packing_list", label: "Packing List" },
                    { type: "other", label: "Other" },
                  ] as const).map((section) => (
                    <div key={section.type}>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm text-muted-foreground">{section.label}</Label>
                        <label>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                void handleUploadPoFile(section.type, file);
                              }
                              event.target.value = "";
                            }}
                            disabled={uploadingFileType === section.type}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            asChild
                          >
                            <span>
                              {uploadingFileType === section.type ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" />
                              )}
                            </span>
                          </Button>
                        </label>
                      </div>
                      {(filesByType[section.type] ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No file</p>
                      ) : (
                        <div className="space-y-1">
                          {filesByType[section.type].map((row) => (
                            <div key={row.id} className="flex items-center gap-2 text-sm bg-muted rounded px-2 py-1.5">
                              <File className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="truncate flex-1" title={row.file_name}>
                                {row.file_name}
                              </span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {formatBytes(row.file_size)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 shrink-0"
                                onClick={() => handleDownloadPoFile(row.id)}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 shrink-0 text-destructive hover:text-destructive"
                                onClick={() => handleDeletePoFile(row.id, row.file_name)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>}

              {!isTransfer && <Card>
                <CardHeader>
                  <CardTitle>Email History</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {purchaseOrderEmailLogQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading email history…</p>
                  ) : (purchaseOrderEmailLogQuery.data?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">No emails sent yet.</p>
                  ) : (
                    purchaseOrderEmailLogQuery.data?.map((row) => (
                      <div key={row.id} className="flex items-start gap-3 border-b pb-3 last:border-b-0 last:pb-0">
                        {row.status === "sent" ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">
                            {row.status === "sent" ? "Sent" : "Failed"} {formatDate(row.sent_at)}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            To: {row.sent_to.split(",").length} recipient{row.sent_to.split(",").length === 1 ? "" : "s"}
                          </p>
                          {row.sent_by_name ? (
                            <p className="text-xs text-muted-foreground">By: {row.sent_by_name}</p>
                          ) : null}
                          {row.error_message ? (
                            <p className="text-xs text-destructive mt-1 truncate">{row.error_message}</p>
                          ) : null}
                        </div>
                        {canSend ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleOpenEmailDialog}
                            disabled={sendPurchaseOrderEmail.isPending}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>}

              {/* Linked Product Dev advisory chip. Read-only — hides itself
                  when there are no linked product_dev rows for this PO. */}
              {order?.id && (
                <LinkedProductDevCard purchaseOrderId={order.id} />
              )}
            </div>
          </div>
        </main>

        <BackToTopPill />

        <Toaster />

        {/* Email Purchase Order Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Purchase Order to Supplier</DialogTitle>
              <DialogDescription>
                Select the email addresses to send <strong>{order.po_number}</strong> to:
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {supplierEmails.length === 0 && !internalCcEmail && (
                <p className="text-sm text-muted-foreground py-2">
                  No email addresses found. Add an email to the supplier profile or configure an internal CC in Settings.
                </p>
              )}

              {/* Supplier emails */}
              {supplierEmails.map((email) => (
                <label key={email} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={selectedEmails.has(email)}
                    onCheckedChange={(checked) => {
                      setSelectedEmails((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(email);
                        else next.delete(email);
                        return next;
                      });
                    }}
                  />
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{email}</span>
                </label>
              ))}

              {/* Divider between supplier and internal */}
              {supplierEmails.length > 0 && internalCcEmail && (
                <div className="border-t" />
              )}

              {/* Internal CC email */}
              {internalCcEmail && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={selectedEmails.has(internalCcEmail)}
                    onCheckedChange={(checked) => {
                      setSelectedEmails((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(internalCcEmail);
                        else next.delete(internalCcEmail);
                        return next;
                      });
                    }}
                  />
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{internalCcEmail}</span>
                  <Badge variant="outline" className="text-xs">Internal</Badge>
                </label>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={sendPurchaseOrderEmail.isPending || selectedEmails.size === 0}>
                <Mail className="h-4 w-4 mr-2" />
                {selectedEmails.size > 0 ? `Send to ${selectedEmails.size} recipient${selectedEmails.size > 1 ? "s" : ""}` : "Send Email"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isTransfer ? "Delete Transfer" : "Delete Purchase Order"}</DialogTitle>
              <DialogDescription>
                <strong>{order.po_number}</strong> will be permanently
                removed, including all line items. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog
          open={cancelConfirmOpen}
          onOpenChange={setCancelConfirmOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isTransfer ? "Cancel Transfer" : "Cancel Purchase Order"}</DialogTitle>
              <DialogDescription>
                {isTransfer
                  ? <><strong>{order.po_number}</strong> will be marked as cancelled. Stock will be returned to the source location if already submitted.</>
                  : <>Order <strong>{order.po_number}</strong> will be marked as cancelled. Inventory will not be updated.</>
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCancelConfirmOpen(false)}
              >
                Keep Order
              </Button>
              <Button variant="destructive" onClick={handleCancel} disabled={isUpdating}>
                {isUpdating ? "Cancelling..." : "Cancel Order"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Undo Status Dialog */}
        <Dialog
          open={undoConfirmOpen}
          onOpenChange={setUndoConfirmOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isTransfer ? "Undo Transfer Status" : "Undo Order Status"}</DialogTitle>
              <DialogDescription>
                Revert <strong>{order.po_number}</strong> from{" "}
                <strong>{formatStatusLabel(order.status)}</strong> back to{" "}
                <strong>{undoPreviousStatus ? formatStatusLabel(undoPreviousStatus as PurchaseOrder["status"]) : ""}</strong>.
                {isTransfer && order.status === "submitted" && (
                  <> Committed stock at the source location will be released.</>
                )}
                {isTransfer && order.status === "in_transit" && (
                  <> Stock will be returned to the source location and re-committed.</>
                )}
                {(order.status === "partially_received" || order.status === "received") && (
                  <> Any received items will be reversed and stock adjustments undone.</>
                )}
                {!isTransfer && order.status !== "partially_received" && order.status !== "received" && (
                  <> No stock movements will be affected.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUndoConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleUndo} disabled={isUndoing}>
                <Undo2 className="h-4 w-4 mr-2" />
                {isUndoing ? "Reverting..." : "Undo Status"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* BLU-572: Complete PO Confirmation Dialog (short-close a partial) */}
        <Dialog
          open={completeConfirmOpen}
          onOpenChange={setCompleteConfirmOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                {isTransfer ? "Complete transfer?" : "Complete purchase order?"}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2 pt-2 text-sm text-muted-foreground">
                  <p>
                    Mark <strong className="text-foreground">{order.po_number}</strong> as received.
                    The remaining{" "}
                    <span className="font-medium tabular-nums text-foreground">
                      {Math.max(totalOrdered - totalReceived, 0)}
                    </span>{" "}
                    of {totalOrdered} ordered units will be closed and will stop showing as
                    incoming stock.
                  </p>
                  <p>
                    Received quantities are preserved. No stock is moved by this action.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCompleteConfirmOpen(false)}
              >
                Keep as partial
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleComplete}
                disabled={isCompleting}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" aria-hidden="true" />
                {isCompleting ? "Completing…" : isTransfer ? "Complete transfer" : "Complete PO"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* BLU-373 PR3: Adjust unit cost (Tier 1 cost correction) */}
        <AdjustUnitCostDialog
          open={adjustCostItem != null}
          onOpenChange={(o) => {
            if (!o) setAdjustCostItem(null);
          }}
          item={adjustCostItem}
          order={order}
          currentWac={null}
          currentStockAtLocation={null}
          orgBaseCurrency={organization?.default_currency ?? "AUD"}
        />

        {/* BLU-373 PR2: Mark Paid Confirmation Dialog */}
        <Dialog open={markPaidConfirmOpen} onOpenChange={setMarkPaidConfirmOpen}>
          <DialogContent
            onOpenAutoFocus={(e) => {
              // Default Radix focus lands on the destructive action — push focus to Cancel instead
              e.preventDefault();
              const cancel = document.getElementById("mark-paid-cancel-button");
              cancel?.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                Mark as paid?
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2 pt-2 text-sm text-muted-foreground">
                  <p>
                    Record payment on <code className="font-mono text-foreground">{order.po_number}</code>.
                    {order.currency && order.currency !== (organization?.default_currency ?? "AUD") && order.exchange_rate && order.exchange_rate !== 1 ? (
                      <>
                        {" "}This locks the exchange rate at{" "}
                        <span className="font-mono tabular-nums text-foreground">
                          {(order.exchange_rate ?? 1).toFixed(4)}
                        </span>{" "}
                        ({order.currency} → {organization?.default_currency ?? "AUD"}).
                      </>
                    ) : null}
                  </p>
                  <p>
                    After this the exchange rate, line costs, additional costs, and invoice amount
                    can no longer be edited on this order. Receiving stock and changing fulfillment
                    status are still allowed.
                  </p>
                  <p className="text-amber-700">
                    This is permanent. There is no undo.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                id="mark-paid-cancel-button"
                variant="outline"
                onClick={() => setMarkPaidConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={async () => {
                  setIsMarkingPaid(true);
                  try {
                    await handleMarkOrderPaid(order.id, order.location_id);
                    setMarkPaidConfirmOpen(false);
                  } finally {
                    setIsMarkingPaid(false);
                  }
                }}
                disabled={isMarkingPaid}
              >
                <Lock className="h-4 w-4 mr-2" aria-hidden="true" />
                {isMarkingPaid ? "Marking Paid…" : "Mark Paid & Lock Rate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ship Transfer: a quantity per SKU, not a status flip */}
        <ShipTransferDialog
          open={shipConfirmOpen}
          onOpenChange={setShipConfirmOpen}
          order={order}
          items={sortedItems}
          sourceLocationName={sourceLocationName ?? null}
          destLocationName={destLocationName ?? null}
          isShipping={isShipping}
          onShip={handleShipTransfer}
        />

        {/* Create Shipment Dialog */}
        <Dialog open={createShipmentOpen} onOpenChange={setCreateShipmentOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Create Shipment</DialogTitle>
              <DialogDescription>
                Create a new shipment linked to <strong>{order.po_number}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label>Freight Forwarder</Label>
                <Select
                  value={newShipmentForwarderId || "__none__"}
                  onValueChange={(value) => setNewShipmentForwarderId(value === "__none__" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select forwarder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No forwarder</SelectItem>
                    {(forwardersQuery.data ?? []).map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Tracking Link</Label>
                <Input
                  value={newShipmentTrackingLink}
                  onChange={(e) => setNewShipmentTrackingLink(e.target.value)}
                  placeholder="URL or tracking number"
                />
              </div>
              <div className="grid gap-2">
                <Label>Freight Cost (AUD)</Label>
                <Input
                  inputMode="decimal"
                  value={newShipmentFreightCost}
                  onChange={(e) => setNewShipmentFreightCost(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateShipmentOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!orderLocationId) return;
                  createShipment.mutate({
                    locationId: orderLocationId,
                    freightForwarderId: newShipmentForwarderId || null,
                    status: "pending",
                    shippedDate: null,
                    expectedDeliveryDate: null,
                    weight: null,
                    cbm: null,
                    trackingLink: newShipmentTrackingLink.trim() || null,
                    freightCost: newShipmentFreightCost.trim() ? Number(newShipmentFreightCost) : null,
                    currency: "AUD",
                    exchangeRate: 1,
                    notes: null,
                    purchaseOrderIds: [order.id],
                  });
                }}
                disabled={createShipment.isPending}
              >
                Create Shipment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </RoleGuard>
  );
};

/**
 * BLU-1268: a just-in-time PO (bought to order for a batch, cost only, never
 * received) gets its own focused, mobile-first view instead of this page's
 * receive machinery. Branching here, before OrderDetail mounts, keeps the
 * 2000-line standard page untouched. Same `getOrder` query and cache as
 * OrderDetail, so a standard PO costs no extra request.
 */
function OrderDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const routeLocation = useLocation();
  const { organization } = useOrganization();
  const { order, loading } = useOrderQuery(id);
  // PR #1989 review: start the dates panel's ETA history with the order (same
  // gate as useOrderQuery, so both ride one batched request). Started any
  // later (OrderDetail mounts only once getOrder lands, and its PO list and
  // purchasable SKU reads go first), the "Revised..." line pops in and pushes
  // the card down. Same input and staleTime as the panel's useOrderDates, so
  // the panel reads this cache entry: one request. Transfers carry an Arrival
  // ETA history too, so both routes start it.
  useOrderDateHistory(id, !!organization);

  if (loading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="purchaseOrders" />
        </div>
      </RoleGuard>
    );
  }

  if (isBuyListPoKind(order?.kind) && order && !routeLocation.pathname.startsWith("/transfers/")) {
    const ordersReturnTo = safePurchaseOrderListReturnTo(
      (routeLocation.state as { ordersReturnTo?: unknown } | null)?.ordersReturnTo,
    );
    // BLU-1506: an Order and wait PO has its own page (Send, Arrived, the
    // orders that wait on it); neither buy-list kind runs the receive flow.
    if (order.kind === "order_and_wait") {
      return <OrderAndWaitOrderView key={order.id} order={order} ordersReturnTo={ordersReturnTo} />;
    }
    return <JustInTimeOrderView key={order.id} order={order} ordersReturnTo={ordersReturnTo} />;
  }

  return <OrderDetail />;
}

export default function OrderDetailWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <OrderDetailRoute />
    </PageErrorBoundary>
  );
}
